package com.htmlagent.site.api

import arrow.core.Either
import arrow.core.flatMap
import arrow.core.left
import arrow.core.right
import com.htmlagent.shared.ApiProblem
import com.htmlagent.shared.UiLocale
import com.htmlagent.shared.ApiRoutes
import com.htmlagent.shared.auth.ApproveLinkRequest
import com.htmlagent.shared.auth.GuestRequest
import com.htmlagent.shared.auth.LinkSentResponse
import com.htmlagent.shared.auth.LoginLinkRequest
import com.htmlagent.shared.auth.MeResponse
import com.htmlagent.shared.auth.VerifyLinkRequest
import com.htmlagent.shared.auth.VerifyLinkResponse
import com.htmlagent.shared.auth.WaitResponse
import com.htmlagent.shared.session.ArtifactHistoryResponse
import com.htmlagent.shared.session.EditMessageRequest
import com.htmlagent.shared.session.ImageUpload
import com.htmlagent.shared.session.PostMessageRequest
import com.htmlagent.shared.session.SessionDetailResponse
import com.htmlagent.shared.session.SessionListResponse
import com.htmlagent.shared.session.StartSessionResponse
import com.htmlagent.shared.stream.StreamEvent
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.await
import kotlinx.serialization.DeserializationStrategy
import kotlinx.serialization.json.Json
import org.w3c.dom.EventSource
import org.w3c.dom.EventSourceInit
import org.w3c.dom.MessageEvent
import org.w3c.dom.events.Event
import org.w3c.fetch.INCLUDE
import org.w3c.fetch.RequestCredentials
import org.w3c.fetch.RequestInit

/**
 * HTTP client against the Spring Boot API, using the `:shared` DTOs.
 *
 * It does not use the Kobweb helpers (`window.api`, `ApiFetcher`): they point at a server that
 * does not exist here and would fail with a 404 in production. The `Json` is the same one the
 * backend uses; if they diverge, the shared contract guarantees nothing.
 *
 * Every call answers with [Either] and never throws: a failure is a value the screen has to
 * handle, and the compiler says so.
 *
 * The contract it fulfils is [AgentApi]; this is the implementation that speaks HTTP.
 */
class AgentClient(private val baseUrl: String = "") : AgentApi {

    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
        classDiscriminator = "type"
    }

    /** Whose session it is comes from the session cookie, not from anything sent here. */
    override suspend fun startSession(): Either<ApiFailure, StartSessionResponse> =
        post(ApiRoutes.Sessions.ROOT, body = "{}").flatMap { decode(StartSessionResponse.serializer(), it) }

    /**
     * Queues a turn; the answer arrives over the stream, not from here.
     *
     * A busy session comes back as [ApiFailure.Problem] with `session_busy`, like any other
     * case: what it means to the person is decided by the screen, not here.
     *
     * [locale] travels with the message because it is what the agent answers and writes the
     * page in. Leaving the model to infer it from the request is a guess it loses on a
     * two-word brief.
     */
    override suspend fun sendMessage(
        sessionId: String,
        prompt: String,
        locale: UiLocale,
        images: List<ImageUpload>,
    ): Either<ApiFailure, Unit> =
        post(
            ApiRoutes.Sessions.messages(sessionId),
            json.encodeToString(
                PostMessageRequest.serializer(),
                PostMessageRequest(
                    prompt,
                    locale,
                    browserLocale = window.navigator.language,
                    timeZone = browserTimeZone(),
                    images = images,
                ),
            ),
        ).map { }

    /**
     * Stops the turn in flight.
     *
     * Closing the tab does not stop it: the loop lives in the application scope precisely so it
     * survives the client going away.
     */
    override suspend fun cancel(sessionId: String): Either<ApiFailure, Unit> =
        post(ApiRoutes.Sessions.cancel(sessionId), body = null).map { }

    /**
     * `DELETE` rewinds; `PATCH` rewinds and sends the new text as a fresh turn, whose answer, as
     * with [sendMessage], arrives over the stream.
     */
    override suspend fun rewind(sessionId: String, messageId: Long, resend: Resend?): Either<ApiFailure, Unit> =
        send(
            ApiRoutes.Sessions.message(sessionId, messageId),
            if (resend == null) {
                RequestInit(method = "DELETE")
            } else {
                RequestInit(
                    method = "PATCH",
                    headers = jsonHeaders(),
                    body = json.encodeToString(
                        EditMessageRequest.serializer(),
                        EditMessageRequest(
                            resend.text,
                            resend.locale,
                            window.navigator.language,
                            browserTimeZone(),
                            resend.keptImages,
                        ),
                    ),
                )
            },
        ).map { }

    /** Who this browser is signed in as; a 401 when it is nobody yet. */
    suspend fun me(): Either<ApiFailure, MeResponse> =
        get(ApiRoutes.Auth.me()).flatMap { decode(MeResponse.serializer(), it) }

    /**
     * Emails an access link and answers the number to show while waiting for it. Once the link is
     * opened, here or approved from another browser, a guest's pages move to that address, because
     * the guest's cookie travels with the sign-in.
     */
    suspend fun requestLink(email: String, locale: UiLocale): Either<ApiFailure, LinkSentResponse> =
        post(
            ApiRoutes.Auth.link(),
            json.encodeToString(LoginLinkRequest.serializer(), LoginLinkRequest(email, locale)),
        ).flatMap { decode(LinkSentResponse.serializer(), it) }

    /**
     * Trades the token of an emailed link for the session cookie. In a browser other than the one
     * that asked for it, it answers the numbers to approve that one with instead.
     */
    suspend fun verify(token: String): Either<ApiFailure, VerifyLinkResponse> =
        post(
            ApiRoutes.Auth.verify(),
            json.encodeToString(VerifyLinkRequest.serializer(), VerifyLinkRequest(token)),
        ).flatMap { decode(VerifyLinkResponse.serializer(), it) }

    /** Lets the browser waiting for the link in, with the number it shows. A wrong one spends the link. */
    suspend fun approve(token: String, match: Int): Either<ApiFailure, Unit> =
        post(
            ApiRoutes.Auth.approve(),
            json.encodeToString(ApproveLinkRequest.serializer(), ApproveLinkRequest(token, match)),
        ).map { }

    /**
     * Asked by the browser waiting for its link: who it is once approved, or the address and number
     * it is waiting with. `invalid_link` when this browser has no link to wait for.
     */
    suspend fun awaitApproval(): Either<ApiFailure, WaitResponse> =
        post(ApiRoutes.Auth.await(), "{}").flatMap { decode(WaitResponse.serializer(), it) }

    /** A guest session: a new guest, or the one [recoveryKey] names. */
    suspend fun guest(recoveryKey: String? = null): Either<ApiFailure, MeResponse> =
        post(ApiRoutes.Auth.guest(), json.encodeToString(GuestRequest.serializer(), GuestRequest(recoveryKey)))
            .flatMap { decode(MeResponse.serializer(), it) }

    /** Closes the session on the server too, so a copied cookie stops working. */
    suspend fun logout(): Either<ApiFailure, Unit> = post(ApiRoutes.Auth.logout(), body = null).map { }

    /** The caller's sessions, from the most recent to the oldest. */
    override suspend fun sessions(): Either<ApiFailure, SessionListResponse> =
        get(ApiRoutes.Sessions.ROOT).flatMap { decode(SessionListResponse.serializer(), it) }

    /**
     * Deletes a session along with its conversation, its versions and its events. It is
     * irreversible: confirmation is asked for before calling here.
     */
    override suspend fun deleteSession(sessionId: String): Either<ApiFailure, Unit> =
        send(ApiRoutes.Sessions.byId(sessionId), RequestInit(method = "DELETE")).map { }

    /** An earlier session, with its conversation, so it can be continued. */
    override suspend fun session(sessionId: String): Either<ApiFailure, SessionDetailResponse> =
        get(ApiRoutes.Sessions.byId(sessionId)).flatMap { decode(SessionDetailResponse.serializer(), it) }

    /**
     * A version's code, as text, for the code panel.
     *
     * It goes against the API and not the artifact origin, which carries no CORS on purpose:
     * that origin isolates the HTML's execution; reading it to paint it is another matter.
     */
    override suspend fun versionCode(sessionId: String, version: Int): Either<ApiFailure, String> =
        get(ApiRoutes.Sessions.versionCode(sessionId, version))

    override suspend fun versions(sessionId: String): Either<ApiFailure, ArtifactHistoryResponse> =
        get(ApiRoutes.Sessions.versions(sessionId)).flatMap { decode(ArtifactHistoryResponse.serializer(), it) }

    /**
     * Opens a session's event stream.
     *
     * `EventSource` and not `fetch`: it reconnects on its own and sends `Last-Event-ID`, which is
     * half the replay mechanism; the backend supplies the other half by numbering every event.
     *
     * `liveOnly` goes to `true` when reopening a session whose conversation was already loaded
     * from history; without it the replay repeats the whole thread and the agent's message
     * shows up twice.
     */
    override fun openStream(
        sessionId: String,
        onEvent: (StreamEvent) -> Unit,
        onLost: () -> Unit,
        liveOnly: Boolean,
    ): AgentStream {
        val route = if (liveOnly) ApiRoutes.Sessions.liveStream(sessionId)
        else ApiRoutes.Sessions.stream(sessionId)
        // With credentials, or the session cookie stays behind when the API is on another port.
        val source = EventSource(baseUrl + route, EventSourceInit(withCredentials = true))

        source.onmessage = { event: MessageEvent ->
            val data = event.data as? String
            if (data != null) {
                // An unknown event (newer backend) must not bring the stream down.
                Either.catch { json.decodeFromString(StreamEvent.serializer(), data) }
                    .onRight(onEvent)
            }
        }

        source.onerror = { _: Event ->
            // `EventSource` retries on its own; only report it if it closed for good.
            if (source.readyState == EventSource.CLOSED) onLost()
        }

        return AgentStream { source.close() }
    }

    private suspend fun get(route: String): Either<ApiFailure, String> = send(route, init = null)

    private suspend fun post(route: String, body: String?): Either<ApiFailure, String> =
        send(
            route,
            RequestInit(method = "POST", headers = jsonHeaders(), body = body),
        )

    /**
     * The one place that talks to the network. A non-OK answer is read as [ApiProblem] before
     * anything else: the backend already wrote there which error it is and what to say about it,
     * and throwing that away is what left the person looking at "HTTP 500".
     *
     * The cancellation is rethrown instead of becoming a failure: leaving the screen cancels
     * these coroutines, and reporting that as an error would put a message in the chat for
     * something the person did on purpose.
     */
    private suspend fun send(route: String, init: RequestInit?): Either<ApiFailure, String> {
        // The session cookie has to travel on every call, also in development, where the API
        // is another origin and the browser leaves it behind unless told otherwise.
        val request = (init ?: RequestInit()).apply { credentials = RequestCredentials.INCLUDE }
        val response = try {
            window.fetch(baseUrl + route, request).await()
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (failure: Throwable) {
            return ApiFailure.Unreachable(failure).left()
        }

        val body = try {
            response.text().await()
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (failure: Throwable) {
            return ApiFailure.Unreachable(failure).left()
        }

        val status = response.status.toInt()
        return if (response.ok) body.right() else problemOf(status, body).left()
    }

    private fun problemOf(status: Int, body: String): ApiFailure =
        Either.catch { json.decodeFromString(ApiProblem.serializer(), body) }
            .fold({ ApiFailure.Unexpected(status) }, { ApiFailure.Problem(status, it) })

    private fun <T> decode(serializer: DeserializationStrategy<T>, body: String): Either<ApiFailure, T> =
        Either.catch { json.decodeFromString(serializer, body) }
            .mapLeft { ApiFailure.Malformed(it) }

    private fun jsonHeaders() = org.w3c.fetch.Headers().apply {
        append("Content-Type", "application/json")
        append("Accept", "application/json")
    }
}

/**
 * The browser's IANA time zone (`America/Bogota`): with the locale, what tells the agent which
 * country the invented facts of a page belong to. `null` where the browser does not say.
 */
private fun browserTimeZone(): String? =
    js("Intl.DateTimeFormat().resolvedOptions().timeZone") as? String
